'use client'

import { AppProvider, Frame, Spinner, Toast } from '@shopify/polaris'
import '@shopify/polaris/build/esm/styles.css'
import { usePathname } from 'next/navigation'
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { AppNav } from '@/components/AppNav'
import { PortalAppShellFooter } from '@/components/PortalAppShellFooter'
import { AppErrorBoundary } from '@/components/AppErrorBoundary'
import { PolarisLink } from '@/components/PolarisLink'
import { useAppBridgeReady, useAuthenticatedFetch } from '@/app/(app)/hooks/useAuthenticatedFetch'
import { redirectToUrl, getStoreId } from '@/app/(app)/hooks/useAppBridgeRedirect'
import { useBillingRedirects } from '@/app/(app)/hooks/useBillingRedirects'
import { BootProvider, useBoot } from '@/app/(app)/context/BootContext'
import { normalizeShopDomain } from '@/lib/shop-domain'
import { AppBridgeReadyProvider } from '@/app/(app)/context/AppBridgeReadyContext'
import { ToastProvider, useToast } from '@/app/(app)/context/ToastContext'
import { BootstrapProvider, useBootstrap } from '@/app/(app)/context/BootstrapContext'
import { SigmaQueryProvider } from '@/lib/query/QueryProvider'
import { isPortalSurfaceClient } from '@/lib/portal-surface-client'
import { createPortalOptimisticBootstrap } from '@/lib/portal-optimistic-bootstrap'
import type { BootstrapData, BootstrapLimits, BootstrapPlan, BootstrapUsage, BootstrapDashboard } from '@/app/(app)/context/BootstrapContext'
import { parseResponseJson } from '@/lib/client/parseResponseJson'
import enTranslations from '@shopify/polaris/locales/en.json'

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.sigmasense.pk'
const CLIENT_DEBUG_LATENCY =
  process.env.NEXT_PUBLIC_DEBUG_LATENCY === '1' || process.env.NEXT_PUBLIC_SIGMA_PERF_LOG === '1'
const BOOTSTRAP_CACHE_TTL_MS = 10 * 60 * 1000
const BOOTSTRAP_CLIENT_TIMEOUT_MS = 15_000
const BOOTSTRAP_ERROR_USER_MESSAGE =
  'Unable to load app data. This may be a temporary issue.'
const publicPaths = ['/contact', '/get-started', '/signin', '/billing']

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true
  if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'AbortError') {
    return true
  }
  return false
}

type BootstrapCacheEnvelope = {
  savedAt: number
  data: BootstrapData
}

function getBootstrapCacheKey(shop: string, host: string): string {
  return `sigma:bootstrap:${shop}:${host}`
}

function readBootstrapCache(shop: string, host: string): BootstrapData | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(getBootstrapCacheKey(shop, host))
    if (!raw) return null
    const parsed = JSON.parse(raw) as BootstrapCacheEnvelope
    if (!parsed?.data || !parsed?.savedAt) return null
    if (Date.now() - parsed.savedAt > BOOTSTRAP_CACHE_TTL_MS) return null
    const pr = parsed.data.planResolution
    if (pr !== 'known' && pr !== 'unknown') {
      clearBootstrapCache(shop, host)
      return null
    }
    return parsed.data
  } catch {
    return null
  }
}

function writeBootstrapCache(shop: string, host: string, data: BootstrapData): void {
  if (typeof window === 'undefined') return
  try {
    const payload: BootstrapCacheEnvelope = { savedAt: Date.now(), data }
    window.sessionStorage.setItem(getBootstrapCacheKey(shop, host), JSON.stringify(payload))
  } catch {
    // Ignore cache write failures.
  }
}

function clearBootstrapCache(shop: string, host: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(getBootstrapCacheKey(shop, host))
  } catch {
    // Ignore cache clear failures.
  }
}

function BootingLoader() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        gap: '12px',
      }}
    >
      <Spinner size="large" />
      <span style={{ color: 'var(--p-color-text-secondary)', fontSize: '13px' }}>Loading SigmaSense…</span>
    </div>
  )
}

function AppLayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const isMetricBreakdownPrintPath = pathname?.startsWith('/metric-breakdown/print') ?? false
  const { bootHost, bootShop, bootReady } = useBoot()
  const appBridgeReady = useAppBridgeReady()
  const hasRedirectedRef = useRef(false)

  const isPublicPath = useMemo(() => publicPaths.includes(pathname), [pathname])
  const effectiveHost = bootHost
  const effectiveShop = bootShop

  const isEmbeddedMode = !!(effectiveShop && effectiveHost)
  const isDirectMode = !!effectiveShop && !effectiveHost

  // TEMP: URL token audit (behind env flag, safe to remove)
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_URL_TOKEN_AUDIT !== '1') return
    if (typeof window === 'undefined') return
    if ((window as any).__sigmaUrlTokenAuditLogged) return
    ;(window as any).__sigmaUrlTokenAuditLogged = true

    const url = new URL(window.location.href)
    const params = url.searchParams
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))

    const candidates = ['id_token', 'session_token', 'token'] as const
    const tokens = candidates.map((name) => {
      const value = params.get(name) || hashParams.get(name)
      return {
        name,
        exists: !!value,
        length: value ? value.length : 0,
      }
    })

    const hasIdToken = tokens.find((t) => t.name === 'id_token')?.exists ?? false
    const hasSessionToken = tokens.find((t) => t.name === 'session_token')?.exists ?? false
    const hasToken = tokens.find((t) => t.name === 'token')?.exists ?? false

    // eslint-disable-next-line no-console
    console.log('[sigma:url-token-audit] initial', {
      path: url.pathname,
      hasIdToken,
      hasSessionToken,
      hasToken,
      tokens,
    })
  }, [])

  // Set needsRedirect when boot complete but no shop (and not on public path)
  const needsRedirect = bootReady && !effectiveShop && !isPublicPath
  // When embedded (have host), must wait for App Bridge before redirect
  const canRedirect = needsRedirect && (!effectiveHost || appBridgeReady)

  // Single redirect: only in useEffect, guarded by hasRedirectedRef, after App Bridge ready when embedded
  useEffect(() => {
    if (!canRedirect || hasRedirectedRef.current) return
    hasRedirectedRef.current = true
    const dest =
      typeof window !== 'undefined' && isPortalSurfaceClient()
        ? `${window.location.origin.replace(/\/$/, '')}/login`
        : `${APP_URL.replace(/\/$/, '')}/signin`
    redirectToUrl(dest)
  }, [canRedirect])

  // Booting: show loader until we have effectiveShop and (for embedded) effectiveHost + App Bridge ready
  const showBooting =
    !bootReady ||
    !effectiveShop ||
    (isEmbeddedMode && (!effectiveHost || !appBridgeReady))

  if (showBooting) {
    if (isPublicPath && !effectiveShop) {
      return (
        <AppProvider i18n={enTranslations} linkComponent={PolarisLink as any}>
          {children}
        </AppProvider>
      )
    }
    return (
      <AppProvider i18n={enTranslations} linkComponent={PolarisLink as any}>
        <BootingLoader />
      </AppProvider>
    )
  }

  if (isEmbeddedMode && effectiveShop && effectiveHost) {
    return (
      <BootstrapGate
        shop={effectiveShop}
        host={effectiveHost}
        isPublicPath={isPublicPath}
        variant={isMetricBreakdownPrintPath ? 'print' : 'default'}
      >
        {children}
      </BootstrapGate>
    )
  }

  if (effectiveShop && !effectiveHost) {
    return (
      <BootstrapGate
        shop={effectiveShop}
        host={null}
        isPublicPath={isPublicPath}
        variant={isMetricBreakdownPrintPath ? 'print' : 'default'}
      >
        {children}
      </BootstrapGate>
    )
  }

  if (isMetricBreakdownPrintPath) {
    return (
      <AppProvider i18n={enTranslations} linkComponent={PolarisLink as any}>
        <div className="sigma-metric-print-shell sigma-metric-print-shell--layout">
          {children}
        </div>
      </AppProvider>
    )
  }

  return (
    <AppProvider i18n={enTranslations} linkComponent={PolarisLink as any}>
      <ToastProvider>
        <FrameWithToast>
          {children}
        </FrameWithToast>
      </ToastProvider>
    </AppProvider>
  )
}

function FrameWithToast({
  children,
  isPublicPath,
  contentOnly,
  shellClassName,
}: {
  children: React.ReactNode
  isPublicPath?: boolean
  /** When true, skip BillingGate here (caller wraps billing around page content only). */
  contentOnly?: boolean
  shellClassName?: string
}) {
  const { toast, dismissToast } = useToast()
  const toastMarkup =
    toast ? (
      <Toast
        content={toast.content}
        onDismiss={dismissToast}
        error={toast.tone === 'critical'}
      />
    ) : null

  const shellBody =
    isPublicPath !== undefined && !contentOnly ? (
      <BillingGate isPublicPath={isPublicPath}>{children}</BillingGate>
    ) : (
      children
    )

  return (
    <Frame>
      <div className={['sigma-app-shell', shellClassName].filter(Boolean).join(' ')}>{shellBody}</div>
      {toastMarkup}
    </Frame>
  )
}

interface BootstrapGateProps {
  children: React.ReactNode
  shop: string
  /** Shopify Admin host; null for cookie sessions (portal / merchant direct without host). */
  host: string | null
  isPublicPath: boolean
  variant?: 'default' | 'print'
}

function BootstrapGate({ children, shop, host, isPublicPath, variant = 'default' }: BootstrapGateProps) {
  const appBridgeReady = useAppBridgeReady()
  const authenticatedFetch = useAuthenticatedFetch()
  const { redirectToManagePlan } = useBillingRedirects()
  const boot = useBoot()
  const useShopifyHost = !!(host && host.trim())
  const portalFastPath = isPortalSurfaceClient() && !useShopifyHost
  const [state, setState] = useState<{
    loading: boolean
    error: string | null
    reauthUrl: string | null
    data: BootstrapData | null
  }>({ loading: true, error: null, reauthUrl: null, data: null })
  const hasRunRef = useRef(false)
  const portalSeededShopRef = useRef<string | null>(null)
  const redirectingRef = useRef(false)
  const [retryToken, setRetryToken] = useState(0)
  const requestStartedAtRef = useRef<number | null>(null)

  useEffect(() => {
    hasRunRef.current = false
  }, [shop])

  /** Portal: paint shell + routes immediately; real bootstrap replaces payload in background. */
  useLayoutEffect(() => {
    if (!shop || !portalFastPath) return
    if (portalSeededShopRef.current === shop) return
    portalSeededShopRef.current = shop
    const cacheHost = '__cookie__'
    const cached = readBootstrapCache(shop, cacheHost)
    setState({
      loading: false,
      error: null,
      reauthUrl: null,
      data: cached ?? createPortalOptimisticBootstrap(),
    })
  }, [shop, portalFastPath])

  const logClientTrace = useCallback((event: string, meta: Record<string, unknown> = {}) => {
    if (!CLIENT_DEBUG_LATENCY) return
    // eslint-disable-next-line no-console
    console.log('[trace][bootstrap-client]', { event, shop, host, ...meta })
  }, [shop, host])

  useEffect(() => {
    if (!shop || !appBridgeReady || hasRunRef.current) return
    if (useShopifyHost && !host?.trim()) return
    if (portalFastPath && boot.portalSessionStatus === 'loading') return
    hasRunRef.current = true

    const cacheHost = useShopifyHost && host ? host : '__cookie__'

    let cancelled = false
    const mainController = new AbortController()
    const bgController = new AbortController()
    let mainTimeoutId = 0 as number
    let bgTimeoutId = 0 as number
    let scheduleId: number | null = null
    let scheduledViaIdleCallback = false

    const base = typeof window !== 'undefined' ? window.location.origin : ''
    const url =
      useShopifyHost && host
        ? `${base}/api/bootstrap?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
        : `${base}/api/bootstrap?shop=${encodeURIComponent(shop)}`
    const cachedData = readBootstrapCache(shop, cacheHost)
    const portalHydrated =
      portalFastPath &&
      boot.portalSessionStatus === 'done' &&
      boot.portalSessionBootstrap &&
      boot.portalBootstrapShop &&
      normalizeShopDomain(boot.portalBootstrapShop) === normalizeShopDomain(shop)
        ? boot.portalSessionBootstrap
        : null
    const seedBootstrap = portalHydrated ?? cachedData

    const clearTimer = (id: number) => {
      if (id && typeof window !== 'undefined') window.clearTimeout(id)
    }

    const consumeBootstrapResponse = async (res: Response) => {
      if (cancelled) return
      const durationMs = requestStartedAtRef.current ? Date.now() - requestStartedAtRef.current : null
      const data = (await parseResponseJson(res)) as {
        reauth?: boolean
        reauthUrl?: string
        sessionReady?: boolean
        billingActive?: boolean
        plan?: { status?: string } | unknown
        planNormalized?: string
        features?: Record<string, boolean>
        usage?: unknown
        limits?: unknown
        dashboard?: unknown
        dashboardDefaultRange?: { from: string; to: string }
        _fallback?: boolean
        planResolution?: string
      }
      if (cancelled) return
      const fallback = (data as Record<string, unknown>)._fallback === true
      const planStatus = typeof (data.plan as { status?: unknown } | undefined)?.status === 'string'
        ? String((data.plan as { status?: string }).status).toUpperCase()
        : null
      const definitiveBillingInactive = data.billingActive === false && !fallback && planStatus !== 'ACTIVE'
      logClientTrace('request_done', {
        status: res.status,
        durationMs,
        traceId: res.headers.get('x-sigma-trace-id'),
        serverTiming: res.headers.get('server-timing'),
        reauth: data.reauth === true,
        sessionReady: data.sessionReady === true,
        billingActive: data.billingActive !== false,
        fallback,
        planStatus,
        definitiveBillingInactive,
      })
      if (res.status === 401 || data.reauth === true) {
        let reauthUrl: string
        if (!useShopifyHost && typeof window !== 'undefined' && isPortalSurfaceClient()) {
          reauthUrl = `${window.location.origin.replace(/\/$/, '')}/login`
        } else {
          reauthUrl =
            data.reauthUrl ||
            (useShopifyHost && host
              ? `${APP_URL.replace(/\/$/, '')}/api/auth?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
              : `${APP_URL.replace(/\/$/, '')}/signin`)
        }
        clearBootstrapCache(shop, cacheHost)
        logClientTrace('reauth_redirect', { reason: 'bootstrap_401_or_reauth', reauthUrl })
        if (!redirectingRef.current) {
          redirectingRef.current = true
          redirectToUrl(reauthUrl, { host: host ?? undefined, shop })
        }
        setState({ loading: false, error: 'Unauthorized', reauthUrl, data: null })
        return
      }
      if (!data.sessionReady || definitiveBillingInactive) {
        clearBootstrapCache(shop, cacheHost)
        if (definitiveBillingInactive && !redirectingRef.current) {
          redirectingRef.current = true
          const storeId = getStoreId()
          if (storeId) redirectToManagePlan(storeId)
          logClientTrace('billing_redirect', { reason: 'billing_inactive', storeId: !!storeId })
        }
        setState({ loading: false, error: null, reauthUrl: data.reauthUrl ?? null, data: null })
        return
      }
      const nextData: BootstrapData = {
        sessionReady: true,
        billingActive: !definitiveBillingInactive,
        plan: data.plan as BootstrapPlan,
        planNormalized: data.planNormalized ?? 'free',
        planResolution: data.planResolution === 'unknown' ? 'unknown' : 'known',
        features: data.features ?? {},
        usage: (data.usage ?? null) as BootstrapUsage | null,
        limits: data.limits as BootstrapLimits,
        dashboard: (data.dashboard as BootstrapDashboard | null) ?? null,
        dashboardDefaultRange: data.dashboardDefaultRange ?? null,
        adminAllowed: (data as Record<string, unknown>).adminAllowed === true,
      }
      setState({
        loading: false,
        error: null,
        reauthUrl: null,
        data: nextData,
      })
      writeBootstrapCache(shop, cacheHost, nextData)
      if ((!nextData.billingActive || fallback) && !portalFastPath) {
        const origin = typeof window !== 'undefined' ? window.location.origin : ''
        const revalidateUrl =
          useShopifyHost && host
            ? `${origin}/api/billing/subscription-check?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
            : `${origin}/api/billing/subscription-check?shop=${encodeURIComponent(shop)}`
        authenticatedFetch(revalidateUrl).catch(() => {})
      }
    }

    const handleFetchError = (err: unknown, hadCachedBootstrap: boolean, signal: AbortSignal) => {
      if (cancelled) return
      if (isAbortError(err)) {
        logClientTrace('request_error', { message: 'aborted_or_timeout', aborted: signal.aborted })
        if (hadCachedBootstrap) return
        // eslint-disable-next-line no-console
        console.error('[bootstrap] request aborted or timed out', err)
        setState({ loading: false, error: BOOTSTRAP_ERROR_USER_MESSAGE, reauthUrl: null, data: null })
        return
      }
      const technical =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
          ? String((err as { message: string }).message)
          : 'Bootstrap failed. Please try again.'
      logClientTrace('request_error', { message: technical })
      // eslint-disable-next-line no-console
      console.error('[bootstrap] request failed', err)
      if (hadCachedBootstrap) return
      setState({ loading: false, error: BOOTSTRAP_ERROR_USER_MESSAGE, reauthUrl: null, data: null })
    }

    if (seedBootstrap) {
      setState({ loading: false, error: null, reauthUrl: null, data: seedBootstrap })
      if (portalHydrated) {
        writeBootstrapCache(shop, cacheHost, portalHydrated)
        logClientTrace('portal_session_hydrate', { shop })
      } else {
        logClientTrace('cache_hit', { ageHint: 'under_10m' })
      }

      const runBackgroundRevalidate = () => {
        if (cancelled) return
        requestStartedAtRef.current = Date.now()
        logClientTrace('request_start', { url, retryToken, phase: 'background_revalidate' })
        bgTimeoutId =
          typeof window !== 'undefined'
            ? window.setTimeout(() => bgController.abort(), BOOTSTRAP_CLIENT_TIMEOUT_MS)
            : 0
        void authenticatedFetch(url, { signal: bgController.signal })
          .then(async (res) => {
            clearTimer(bgTimeoutId)
            bgTimeoutId = 0
            await consumeBootstrapResponse(res)
          })
          .catch((err: unknown) => {
            clearTimer(bgTimeoutId)
            bgTimeoutId = 0
            handleFetchError(err, true, bgController.signal)
          })
      }

      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        scheduledViaIdleCallback = true
        scheduleId = window.requestIdleCallback(() => runBackgroundRevalidate(), { timeout: 2500 })
      } else if (typeof window !== 'undefined') {
        scheduledViaIdleCallback = false
        scheduleId = window.setTimeout(() => runBackgroundRevalidate(), 0) as unknown as number
      }

      return () => {
        cancelled = true
        clearTimer(mainTimeoutId)
        clearTimer(bgTimeoutId)
        mainController.abort()
        bgController.abort()
        if (typeof window !== 'undefined' && scheduleId != null) {
          if (scheduledViaIdleCallback && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(scheduleId)
          } else {
            window.clearTimeout(scheduleId)
          }
        }
      }
    }

    mainTimeoutId =
      typeof window !== 'undefined'
        ? window.setTimeout(() => mainController.abort(), BOOTSTRAP_CLIENT_TIMEOUT_MS)
        : 0
    requestStartedAtRef.current = Date.now()
    logClientTrace('request_start', { url, retryToken })

    void authenticatedFetch(url, { signal: mainController.signal })
      .then(async (res) => {
        clearTimer(mainTimeoutId)
        mainTimeoutId = 0
        await consumeBootstrapResponse(res)
      })
      .catch((err: unknown) => {
        clearTimer(mainTimeoutId)
        mainTimeoutId = 0
        handleFetchError(err, false, mainController.signal)
      })

    return () => {
      cancelled = true
      clearTimer(mainTimeoutId)
      clearTimer(bgTimeoutId)
      mainController.abort()
      bgController.abort()
    }
  }, [
    appBridgeReady,
    shop,
    host,
    useShopifyHost,
    authenticatedFetch,
    redirectToManagePlan,
    retryToken,
    logClientTrace,
    portalFastPath,
    boot.portalSessionStatus,
    boot.portalBootstrapShop,
    boot.portalSessionBootstrap,
  ])


  // Handle 401 from fetch (res not in scope in catch)
  useEffect(() => {
    if (state.reauthUrl && !state.loading && !redirectingRef.current) {
      redirectingRef.current = true
      logClientTrace('reauth_redirect', { reason: 'state_reauth_url', reauthUrl: state.reauthUrl })
      redirectToUrl(state.reauthUrl, { host: host ?? undefined, shop })
    }
  }, [state.reauthUrl, state.loading, host, shop, logClientTrace])

  const bootstrapValue = useMemo(
    () => ({
      loading: state.loading,
      error: state.error,
      reauthUrl: state.reauthUrl,
      data: state.data,
    }),
    [state.loading, state.error, state.reauthUrl, state.data]
  )

  const contentColumnStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '240px',
    gap: '12px',
  }

  let embeddedMainColumn: ReactNode
  if (state.loading && !state.data && !state.error) {
    embeddedMainColumn = (
      <div style={contentColumnStyle}>
        <Spinner size="large" />
        <span style={{ color: 'var(--p-color-text-secondary)', fontSize: '13px' }}>Loading SigmaSense…</span>
      </div>
    )
  } else if (!state.data && state.error) {
    const displayMessage =
      state.error === 'Unauthorized' ? state.error : BOOTSTRAP_ERROR_USER_MESSAGE
    const reconnectUrl =
      useShopifyHost && host
        ? `${APP_URL.replace(/\/$/, '')}/api/auth?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
        : typeof window !== 'undefined' && isPortalSurfaceClient()
          ? `${window.location.origin.replace(/\/$/, '')}/login`
          : `${APP_URL.replace(/\/$/, '')}/signin`
    embeddedMainColumn = (
      <div style={contentColumnStyle}>
        <span style={{ color: 'var(--p-color-text-secondary)', fontSize: '13px', textAlign: 'center', maxWidth: '360px' }}>
          {displayMessage}
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
          <button
            type="button"
            style={{
              padding: '6px 16px',
              borderRadius: '8px',
              border: '1px solid var(--sigma-border, #d6deeb)',
              background: 'var(--sigma-surface, #ffffff)',
              color: 'var(--sigma-text, #101828)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            onClick={() => {
              logClientTrace('retry_click', { reason: state.error ?? 'unknown' })
              hasRunRef.current = false
              setState({ loading: true, error: null, reauthUrl: null, data: null })
              setRetryToken((t) => t + 1)
            }}
          >
            Retry
          </button>
          <button
            type="button"
            style={{
              padding: '6px 16px',
              borderRadius: '8px',
              border: 'none',
              background: '#008060',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            onClick={() => {
              logClientTrace('reconnect_click', { reason: state.error ?? 'unknown' })
              redirectToUrl(reconnectUrl, { host: host ?? undefined, shop })
            }}
          >
            Reconnect
          </button>
        </div>
      </div>
    )
  } else if (!state.data) {
    embeddedMainColumn = (
      <div style={contentColumnStyle}>
        <Spinner size="large" />
        <span style={{ color: 'var(--p-color-text-secondary)', fontSize: '13px' }}>Loading SigmaSense…</span>
      </div>
    )
  } else {
    embeddedMainColumn = <BillingGate isPublicPath={isPublicPath}>{children}</BillingGate>
  }

  if (useShopifyHost && !appBridgeReady) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: '12px' }}>
        <Spinner size="large" />
        <span style={{ color: 'var(--p-color-text-secondary)', fontSize: '13px' }}>Connecting to Shopify…</span>
      </div>
    )
  }

  return (
    <BootstrapProvider value={bootstrapValue}>
      <SigmaQueryProvider>
        <AppProvider i18n={enTranslations} linkComponent={PolarisLink as any}>
          {variant === 'print' ? (
            <div className="sigma-metric-print-shell sigma-metric-print-shell--layout">{embeddedMainColumn}</div>
          ) : (
            <ToastProvider>
              <div className={portalFastPath ? 'sigma-portal-chrome' : undefined}>
                <AppNav />
                <div className={portalFastPath ? 'sigma-portal-chrome__main' : undefined}>
                  <FrameWithToast
                    isPublicPath={isPublicPath}
                    contentOnly
                    shellClassName={portalFastPath ? 'sigma-app-shell--portal' : undefined}
                  >
                    {embeddedMainColumn}
                  </FrameWithToast>
                </div>
                {portalFastPath ? <PortalAppShellFooter /> : null}
              </div>
            </ToastProvider>
          )}
        </AppProvider>
      </SigmaQueryProvider>
    </BootstrapProvider>
  )
}

function BillingGate({ children, isPublicPath }: { children: React.ReactNode; isPublicPath: boolean }) {
  const bootstrap = useBootstrap()
  const { redirectToManagePlan } = useBillingRedirects()
  const storeId = getStoreId()
  const redirectingRef = useRef(false)

  useEffect(() => {
    if (isPublicPath) return
    if (bootstrap.data?.planResolution === 'unknown') return
    if (!bootstrap.loading && bootstrap.data && !bootstrap.data.billingActive && storeId && !redirectingRef.current) {
      redirectingRef.current = true
      redirectToManagePlan(storeId)
    }
  }, [bootstrap.loading, bootstrap.data, storeId, isPublicPath, redirectToManagePlan])

  if (
    !isPublicPath &&
    !bootstrap.loading &&
    bootstrap.data &&
    bootstrap.data.planResolution !== 'unknown' &&
    !bootstrap.data.billingActive &&
    storeId
  ) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: '12px' }}>
        <Spinner size="large" />
        <span style={{ color: 'var(--p-color-text-secondary)' }}>Redirecting to billing…</span>
      </div>
    )
  }

  return <>{children}</>
}

export function AppLayoutClient({ children }: { children: React.ReactNode }) {
  return (
    <BootProvider>
      <AppErrorBoundary>
        <Suspense
          fallback={
            <div
              style={{
                padding: '16px 24px',
                maxWidth: '1200px',
                margin: '16px auto',
              }}
            >
              <div
                style={{
                  height: '40px',
                  borderRadius: '12px',
                  background: 'var(--sigma-border, #d6deeb)',
                  marginBottom: '16px',
                  opacity: 0.5,
                }}
              />
              <div
                style={{
                  borderRadius: '12px',
                  background: 'var(--sigma-border, #d6deeb)',
                  height: '72px',
                  marginBottom: '12px',
                  opacity: 0.3,
                }}
              />
              <div
                style={{
                  borderRadius: '12px',
                  background: 'var(--sigma-border, #d6deeb)',
                  height: '280px',
                  opacity: 0.2,
                }}
              />
            </div>
          }
        >
          <AppBridgeReadyProvider>
            <AppLayoutContent>{children}</AppLayoutContent>
          </AppBridgeReadyProvider>
        </Suspense>
      </AppErrorBoundary>
    </BootProvider>
  )
}
